export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Game time is shown in UTC unless a zone is given, so it reads the same for everyone. */
export function formatGameTime(iso: string | null | undefined, timeZone = "UTC"): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString("en-GB", { timeZone, year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return d.toISOString();
  }
}

export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const s = Math.round((now - t) / 1000);
  if (Math.abs(s) < 45) return "just now";
  const span = (secs: number) => {
    const m = Math.round(secs / 60);
    if (m < 60) return `${m} min`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} h`;
    const d = Math.round(h / 24);
    return d < 30 ? `${d} d` : null;
  };
  const sp = span(Math.abs(s));
  if (!sp) return formatDateTime(iso);
  return s > 0 ? `${sp} ago` : `in ${sp}`;
}

// A browser's date picker (<input type="datetime-local">) has no time zone: it
// yields a bare wall-clock time like "2040-06-01T09:00". These helpers read and
// write that value in a named IANA zone, usually the game clock's, so what the
// developer types means the same instant whatever zone their own computer is in.

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/** True when timeZone is an IANA zone this browser knows, e.g. "Africa/Lagos". */
export function isValidTimeZone(timeZone: string): boolean {
  try {
    formatter(timeZone);
    return true;
  } catch {
    return false;
  }
}

/** Date.UTC without its quirk of mapping years 0–99 to 1900–1999. */
function utc(y: number, mo: number, d: number, h = 0, mi = 0, s = 0): number {
  const date = new Date(Date.UTC(2000, mo, d, h, mi, s));
  date.setUTCFullYear(y);
  return date.getTime();
}

/** The offset of timeZone at instant t, in ms (wall-clock time minus UTC). Lagos is +3_600_000. */
export function zoneOffsetMs(t: number, timeZone: string): number {
  const parts = formatter(timeZone).formatToParts(new Date(t));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return utc(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second")) - Math.floor(t / 1000) * 1000;
}

const DAY_MS = 86_400_000;

/**
 * Reads a datetime-local value ("2040-06-01T09:00", seconds optional) as a
 * wall-clock time in timeZone and returns the instant as RFC 3339. Returns
 * undefined for a malformed or impossible value (e.g. 30 February) or an
 * unknown zone.
 *
 * Daylight-saving changes follow the usual "compatible" rule (as in Temporal
 * and java.time): a time skipped when clocks go forward moves forward by the
 * gap (02:30 on New York's spring-forward day is 03:30 EDT), and a time that
 * happens twice when clocks go back is the earlier one (01:30 EDT, not EST).
 */
export function zonedInputToIso(value: string, timeZone = "UTC"): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?$/.exec(value);
  if (!m || !isValidTimeZone(timeZone)) return undefined;
  const [y, mo, d, h, mi, s] = [+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] ?? 0)];
  const wall = utc(y, mo, d, h, mi, s);
  const check = new Date(wall);
  if (check.getUTCMonth() !== mo || check.getUTCDate() !== d || check.getUTCHours() !== h || check.getUTCMinutes() !== mi || check.getUTCSeconds() !== s) {
    return undefined; // rolled over: 30 Feb, 24:00, 13th month…
  }
  // The offsets in force a day either side bracket any transition near `wall`.
  const before = zoneOffsetMs(wall - DAY_MS, timeZone);
  const after = zoneOffsetMs(wall + DAY_MS, timeZone);
  const fits = [wall - before, wall - after].filter((t) => t + zoneOffsetMs(t, timeZone) === wall);
  // Two fits: an overlap, take the earlier. None: a gap, so use the offset from
  // before the change, which lands the same distance past it.
  const t = fits.length ? Math.min(...fits) : wall - before;
  return new Date(t).toISOString();
}

/** The reverse: an instant as a datetime-local value ("2040-06-01T09:00") in timeZone. */
export function isoToZonedInput(iso: string | null | undefined, timeZone = "UTC"): string {
  if (!iso || !isValidTimeZone(timeZone)) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  return new Date(t + zoneOffsetMs(t, timeZone)).toISOString().slice(0, 16);
}

/** "in 58 min", "in 3 h", "in 2 d" or "now", from `from` to `iso`. */
export function untilText(iso: string, from: string): string {
  const s = Math.round((new Date(iso).getTime() - new Date(from).getTime()) / 1000);
  if (s <= 0) return "now";
  const m = Math.round(s / 60);
  if (m < 1) return "in under a minute";
  if (m < 60) return `in ${m} min`;
  const h = Math.round(m / 60);
  if (h < 48) return `in ${h} h`;
  return `in ${Math.round(h / 24)} d`;
}

/** A calendar date such as "2 Nov 2026". */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
